import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { cx } from "./cx";
import { IconCheck, IconChevronRight, IconMinus } from "./icons";
import {
  checkState,
  flattenVisible,
  toggleNode,
  typeaheadIndex,
  type FlatTreeItem,
  type TreeNode,
} from "./treeModel";
import { useControllable } from "./useControllable";
import "./Tree.css";

export interface TreeProps {
  nodes: readonly TreeNode[];
  /** Selected leaf ids (channels). Parents derive checked/mixed from their leaves. */
  selected: ReadonlySet<string>;
  onSelectionChange: (next: Set<string>) => void;
  /** Controlled expanded parent ids. */
  expanded?: ReadonlySet<string>;
  onExpandedChange?: (next: Set<string>) => void;
  /** Uncontrolled initial expanded ids. */
  defaultExpanded?: Iterable<string>;
  /** Accessible name of the tree ("Source channels"). */
  label: string;
  /** Enter on a node calls this (instead of toggling selection). */
  onActivate?: (node: TreeNode) => void;
  /** Rendered when `nodes` is empty. */
  empty?: ReactNode;
  className?: string;
  /** Max height before the tree scrolls (CSS length). */
  maxHeight?: string;
}

const EMPTY_SET: ReadonlySet<string> = new Set();

/** Kind glyphs: guild = rounded square with initial, category = folder rule, channel = #. */
function KindIcon({ node }: { node: TreeNode }) {
  if (node.icon) return <span className="ui-tree__kind">{node.icon}</span>;
  if (node.kind === "guild") {
    return (
      <span className="ui-tree__kind ui-tree__kind--guild" aria-hidden="true">
        {node.label.trim().charAt(0).toUpperCase() || "?"}
      </span>
    );
  }
  if (node.kind === "channel") {
    return (
      <span className="ui-tree__kind ui-tree__kind--channel" aria-hidden="true">
        #
      </span>
    );
  }
  return null;
}

/**
 * Checkbox tree (guild → category → channel) with multi-select and full keyboard support:
 * ↑/↓ move, → expand / go to first child, ← collapse / go to parent, Home/End,
 * Space toggles selection, Enter activates (or toggles), type-ahead by label.
 * Uses the flat ARIA tree pattern (aria-level / aria-setsize / aria-posinset).
 */
export function Tree({
  nodes,
  selected,
  onSelectionChange,
  expanded,
  onExpandedChange,
  defaultExpanded,
  label,
  onActivate,
  empty,
  className,
  maxHeight,
}: TreeProps) {
  const initialExpanded = useMemo(
    () => new Set(defaultExpanded ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [openSet, setOpenSet] = useControllable<ReadonlySet<string>>(
    expanded,
    initialExpanded,
    onExpandedChange as ((next: ReadonlySet<string>) => void) | undefined,
  );
  const items = useMemo(() => flattenVisible(nodes, openSet ?? EMPTY_SET), [nodes, openSet]);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const shouldFocus = useRef(false);
  const typed = useRef({ text: "", at: 0 });

  // Keep the roving tab stop on a visible row.
  const activeId =
    focusedId && items.some((i) => i.node.id === focusedId) ? focusedId : (items[0]?.node.id ?? null);

  useEffect(() => {
    if (!shouldFocus.current || !activeId) return;
    shouldFocus.current = false;
    rowRefs.current.get(activeId)?.focus();
  }, [activeId, items]);

  const moveTo = useCallback((id: string) => {
    shouldFocus.current = true;
    setFocusedId(id);
  }, []);

  const setExpanded = useCallback(
    (id: string, open: boolean) => {
      const next = new Set(openSet);
      if (open) next.add(id);
      else next.delete(id);
      setOpenSet(next);
    },
    [openSet, setOpenSet],
  );

  const toggleSelect = useCallback(
    (node: TreeNode) => {
      if (node.disabled) return;
      onSelectionChange(toggleNode(node, selected));
    },
    [onSelectionChange, selected],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const index = items.findIndex((i) => i.node.id === activeId);
    if (index < 0) return;
    const item = items[index];
    let handled = true;
    switch (e.key) {
      case "ArrowDown":
        if (index < items.length - 1) moveTo(items[index + 1].node.id);
        break;
      case "ArrowUp":
        if (index > 0) moveTo(items[index - 1].node.id);
        break;
      case "Home":
        moveTo(items[0].node.id);
        break;
      case "End":
        moveTo(items[items.length - 1].node.id);
        break;
      case "ArrowRight":
        if (item.hasChildren && !item.expanded) setExpanded(item.node.id, true);
        else if (item.hasChildren && items[index + 1]) moveTo(items[index + 1].node.id);
        break;
      case "ArrowLeft":
        if (item.hasChildren && item.expanded) setExpanded(item.node.id, false);
        else if (item.parentId) moveTo(item.parentId);
        break;
      case " ":
        toggleSelect(item.node);
        break;
      case "Enter":
        if (onActivate) onActivate(item.node);
        else toggleSelect(item.node);
        break;
      default:
        handled = false;
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          const now = Date.now();
          typed.current.text = now - typed.current.at > 600 ? e.key : typed.current.text + e.key;
          typed.current.at = now;
          const from = typed.current.text.length > 1 ? index - 1 : index;
          const hit = typeaheadIndex(items, from, typed.current.text);
          if (hit >= 0) {
            moveTo(items[hit].node.id);
            handled = true;
          }
        }
    }
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (items.length === 0) {
    return (
      <div className={cx("ui-tree", "ui-tree--empty", className)} role="tree" aria-label={label}>
        {empty ?? <p className="ui-tree__empty">Nothing to show.</p>}
      </div>
    );
  }

  return (
    <div
      className={cx("ui-tree", className)}
      role="tree"
      aria-label={label}
      aria-multiselectable="true"
      onKeyDown={onKeyDown}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {items.map((item) => (
        <TreeRow
          key={item.node.id}
          item={item}
          state={checkState(item.node, selected)}
          isActive={item.node.id === activeId}
          rowRef={(el) => {
            if (el) rowRefs.current.set(item.node.id, el);
            else rowRefs.current.delete(item.node.id);
          }}
          onFocusRow={() => setFocusedId(item.node.id)}
          onToggleExpand={() => setExpanded(item.node.id, !item.expanded)}
          onToggleSelect={() => toggleSelect(item.node)}
        />
      ))}
    </div>
  );
}

interface TreeRowProps {
  item: FlatTreeItem;
  state: "checked" | "unchecked" | "mixed";
  isActive: boolean;
  rowRef: (el: HTMLDivElement | null) => void;
  onFocusRow: () => void;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
}

function TreeRow({
  item,
  state,
  isActive,
  rowRef,
  onFocusRow,
  onToggleExpand,
  onToggleSelect,
}: TreeRowProps) {
  const { node, level, posinset, setsize, hasChildren: parent, expanded } = item;
  const ariaChecked = state === "mixed" ? "mixed" : state === "checked";
  return (
    <div
      ref={rowRef}
      role="treeitem"
      aria-level={level}
      aria-posinset={posinset}
      aria-setsize={setsize}
      aria-expanded={parent ? expanded : undefined}
      aria-checked={ariaChecked}
      aria-disabled={node.disabled || undefined}
      tabIndex={isActive ? 0 : -1}
      className={cx(
        "ui-tree__row",
        `ui-tree__row--${node.kind}`,
        state !== "unchecked" && "ui-tree__row--selected",
        node.disabled && "ui-tree__row--disabled",
      )}
      style={{ ["--ui-tree-level" as string]: level - 1 }}
      onFocus={onFocusRow}
      onClick={(e) => {
        // Clicking the row toggles selection; the chevron toggles expansion.
        if ((e.target as HTMLElement).closest(".ui-tree__chevron")) return;
        onToggleSelect();
      }}
    >
      <span
        className={cx("ui-tree__chevron", !parent && "ui-tree__chevron--leaf")}
        aria-hidden="true"
        onClick={parent ? onToggleExpand : undefined}
      >
        {parent ? <IconChevronRight className={cx(expanded && "ui-tree__chevron-open")} /> : null}
      </span>
      <span className={cx("ui-tree__check", `ui-tree__check--${state}`)} aria-hidden="true">
        {state === "checked" ? <IconCheck /> : state === "mixed" ? <IconMinus /> : null}
      </span>
      <KindIcon node={node} />
      <span className="ui-tree__label">{node.label}</span>
      {node.meta ? <span className="ui-tree__meta">{node.meta}</span> : null}
    </div>
  );
}
